import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { AccountService } from '../api-services/account.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(private accountService: AccountService, private router: Router) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = localStorage.getItem('token');

    let cloned = req.clone({
      setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    });

    return next.handle(cloned).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (req.url.includes('/refresh-token')) {
            this.forceLogout('Refresh endpoint failed');
            return throwError(() => error);
          }
          return this.handle401Error(cloned, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = localStorage.getItem('token');
    const deviceId = localStorage.getItem('deviceId');

    if (!token || !deviceId) {
      this.forceLogout('Missing token or device ID');
      return throwError(() => new Error('Missing authentication tokens'));
    }

    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(t => t != null),
        take(1),
        switchMap(jwt => next.handle(req.clone({
          setHeaders: { Authorization: `Bearer ${jwt}` },
          withCredentials: true
        })))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.accountService.refreshToken().pipe(
      switchMap((res: any) => {
        this.isRefreshing = false;

        if (!res?.accessToken) {
          this.forceLogout('Invalid refresh response');
          return throwError(() => new Error('Invalid refresh response'));
        }

        localStorage.setItem('token', res.accessToken);
        this.refreshTokenSubject.next(res.accessToken);

        const retryReq = req.clone({
          setHeaders: { Authorization: `Bearer ${res.accessToken}` },
          withCredentials: true
        });

        return next.handle(retryReq);
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.refreshTokenSubject.error(err);
        this.refreshTokenSubject.complete();
        this.forceLogout('Refresh token failed or cookie invalid');
        return throwError(() => err);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  private forceLogout(reason: string): void {
    console.warn('Logging out:', reason);
    localStorage.removeItem('token');
    localStorage.removeItem('deviceId');
    this.accountService.logout();
    this.router.navigate(['/login']);
  }
}
